/**
 * Search Page - Filmssavn
 * Simple search functionality
 */

import { createFooter } from '../components/footer.js';
import { createNavigation } from '../components/navigation.js';
import { createVideoCard } from '../components/video-card.js';
import { api } from '../lib/api.js';
import { t } from '../lib/i18n.js';

export async function renderSearch() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  // Navigation
  app.appendChild(await createNavigation());

  // Search Section
  const searchSection = document.createElement('section');
  searchSection.className = 'section';
  searchSection.style.paddingTop = '100px';
  searchSection.innerHTML = `
    <div class="container">
      <div class="search-container">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input 
            type="text" 
            id="search-input" 
            class="search-input" 
            placeholder="${t('search_placeholder')}"
            autocomplete="off"
          />
        </div>
      </div>
      <div id="search-results" class="video-grid">
        <p class="search-hint" style="color: var(--color-cloud-grey); grid-column: 1/-1; text-align: center; margin-top: var(--spacing-xl);">
          ${t('search_hint')}
        </p>
      </div>
    </div>
  `;
  app.appendChild(searchSection);

  const searchInput = document.getElementById('search-input');
  const resultsContainer = document.getElementById('search-results');
  let searchTimeout;
  let allVideos = [];

  // Load all videos
  try {
    const data = await api.getVideos();
    allVideos = data.videos || [];
  } catch (error) {
    console.error('Failed to load videos:', error);
  }

  // Get initial search query from URL
  const urlParams = new URLSearchParams(window.location.search);
  const initialQuery = urlParams.get('q') || '';
  if (initialQuery) {
    searchInput.value = initialQuery;
    performSearch(initialQuery);
  }

  // Search on input
  searchInput.addEventListener('input', (e) => {
    const query = e.target.value.trim();

    // Clear previous timeout
    clearTimeout(searchTimeout);

    // Update URL without reload
    if (query) {
      window.history.pushState({}, '', `/search?q=${encodeURIComponent(query)}`);
    } else {
      window.history.pushState({}, '', '/search');
    }

    // Debounce search
    searchTimeout = setTimeout(() => {
      performSearch(query);
    }, 300);
  });

  // Focus search input on page load
  searchInput.focus();

  function performSearch(query) {
    if (!query) {
      resultsContainer.innerHTML = `
        <p class="search-hint" style="color: var(--color-cloud-grey); grid-column: 1/-1; text-align: center; margin-top: var(--spacing-xl);">
          ${t('search_hint')}
        </p>
      `;
      return;
    }

    // Show loading
    resultsContainer.innerHTML = `
      <div class="loading-container" style="grid-column: 1/-1;">
        <div class="spinner"></div>
      </div>
    `;

    // Search in all fields: filter videos by all available metadata
    // Normalize whitespace to handle data with trailing spaces or empty middle names
    const searchLower = query.toLowerCase().replace(/\s+/g, ' ').trim();

    // Helper function to convert any value to searchable string
    const toSearchableString = (value) => {
      if (!value) return '';
      if (typeof value === 'string') return value.toLowerCase();
      if (typeof value === 'number') return value.toString();
      if (Array.isArray(value)) {
        return value.map((v) => toSearchableString(v)).join(' ');
      }
      if (typeof value === 'object') {
        return Object.values(value)
          .map((v) => toSearchableString(v))
          .filter((v) => v)
          .join(' ');
      }
      return String(value).toLowerCase();
    };

    const filtered = allVideos.filter((video) => {
      // Get all searchable fields
      const searchableText = [
        video.title,
        video.titleFo,
        video.titleEn,
        video.originalTitle,
        video.internationalTitle,
        video.synopsisFo,
        video.synopsisEn,
        video.synopsis,
        video.loglineFo,
        video.loglineEn,
        video.category,
        video.director,
        video.screenwriter,
        video.producer,
        video.dop,
        video.cinematographer,
        video.editor,
        video.composer,
        video.mainCast,
        video.keyCrew,
        video.productionCompany,
        video.coproductionCompany,
        video.language,
        video.subtitles,
        video.productionYear,
        video.year,
        video.runtime,
      ]
        .map(toSearchableString)
        .join(' ')
        .replace(/\s+/g, ' ');

      return searchableText.includes(searchLower);
    });

    // Render results
    setTimeout(() => {
      renderResults(filtered, query);
    }, 100);
  }

  function renderResults(videos, query) {
    resultsContainer.innerHTML = '';

    if (videos.length > 0) {
      const resultsHeader = document.createElement('div');
      resultsHeader.style.cssText = 'grid-column: 1/-1; margin-bottom: var(--spacing-lg);';
      resultsHeader.innerHTML = `
        <h2 style="color: var(--color-arctic-white); font-size: var(--text-h2);">
          ${t('search_results')}: ${videos.length} ${videos.length === 1 ? t('result') : t('results')}
        </h2>
      `;
      resultsContainer.appendChild(resultsHeader);

      videos.forEach((video) => {
        resultsContainer.appendChild(createVideoCard(video));
      });
    } else {
      resultsContainer.innerHTML = `
        <p class="search-no-results" style="color: var(--color-cloud-grey); grid-column: 1/-1; text-align: center; margin-top: var(--spacing-xl);">
          ${t('search_no_results')}: "${query}"
        </p>
      `;
    }
  }

  // Footer
  app.appendChild(createFooter());
}
